import { RightOutlined } from "@ant-design/icons";
import { Collapse, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { RentCollectionItem } from "~/types/finance";
import { CALENDAR_MONTHS } from "~/utils/constants";
import { RENT_DUE_HEADER_STYLES } from "./constants";
import { RentDueCollapseLabel } from "./RentDueCollapseLabel";
import { RentDueTable } from "./RentDueTable";
import {
  buildRentPeriodLabel,
  filterRentCollectionForPeriod,
  formatRentAmount,
  sumBalanceDue,
} from "./utils";

export type RentCollectionStatusSectionProps = {
  neverPaid: RentCollectionItem[];
  inProgress: RentCollectionItem[];
  month: number;
  year: number;
};

export const RentCollectionStatusSection = ({
  neverPaid,
  inProgress,
  month,
  year,
}: RentCollectionStatusSectionProps) => {
  const { t } = useTranslation();
  const periodLabel = buildRentPeriodLabel(t, month, year, CALENDAR_MONTHS(t));
  const emptyText = t("No_rent_due_records");

  const neverPaidForPeriod = filterRentCollectionForPeriod(
    neverPaid,
    month,
    year,
  );
  const inProgressForPeriod = filterRentCollectionForPeriod(
    inProgress,
    month,
    year,
  );

  const neverPaidTotal = sumBalanceDue(neverPaidForPeriod);
  const inProgressTotal = sumBalanceDue(inProgressForPeriod);

  const collapseItems = [
    {
      key: "never-paid",
      label: (
        <RentDueCollapseLabel
          variant="neverPaid"
          title={t("Rent_Not_Submitted")}
          count={neverPaidForPeriod.length}
          totalBalanceDue={neverPaidTotal}
          periodLabel={periodLabel}
          totalDueLabel={t("Rent_Total_Due", {
            amount: formatRentAmount(neverPaidTotal),
          })}
        />
      ),
      styles: RENT_DUE_HEADER_STYLES.neverPaid,
      children: (
        <RentDueTable
          items={neverPaidForPeriod}
          variant="neverPaid"
          rowKeyPrefix="never-paid"
          emptyText={emptyText}
        />
      ),
    },
    {
      key: "in-progress",
      label: (
        <RentDueCollapseLabel
          variant="inProgress"
          title={t("Rent_In_Progress")}
          count={inProgressForPeriod.length}
          totalBalanceDue={inProgressTotal}
          periodLabel={periodLabel}
          totalDueLabel={t("Rent_Total_Due", {
            amount: formatRentAmount(inProgressTotal),
          })}
        />
      ),
      styles: RENT_DUE_HEADER_STYLES.inProgress,
      children: (
        <RentDueTable
          items={inProgressForPeriod}
          variant="inProgress"
          rowKeyPrefix="in-progress"
          emptyText={emptyText}
        />
      ),
    },
  ];

  if (neverPaidForPeriod.length === 0 && inProgressForPeriod.length === 0) {
    return (
      <Typography.Paragraph type="secondary">
        {t("No_rent_due_for_period", { period: periodLabel })}
      </Typography.Paragraph>
    );
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Collapse
        defaultActiveKey={[]}
        expandIcon={({ isActive }) => (
          <RightOutlined rotate={isActive ? 90 : 0} style={{ marginTop: 10 }} />
        )}
        items={collapseItems}
      />
    </Space>
  );
};
